import { Suspense, type CSSProperties } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AppShell as KitAppShell, AppShellContent, SidebarProvider } from '@shining-technologies/ui';
import { useUiStore } from '@/stores/ui';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { CommandPalette } from './CommandPalette';
import { ImpersonationBanner } from './ImpersonationBanner';
import { IdleTimeout } from './IdleTimeout';
import { ShortcutsOverlay } from './ShortcutsOverlay';
import { ErrorBoundary } from './ErrorBoundary';

/**
 * The admin keeps its 56px header (the kit default is 64px). The kit sizes the
 * app header AND the sidebar header from this one token, so they still line up.
 */
const SHELL_STYLE = { '--sui-shell-header-height': '3.5rem' } as CSSProperties;

/**
 * Persistent app shell: the kit `AppShell` frame (sidebar + sticky header +
 * routed content), with the impersonation banner above it. The kit's
 * `SidebarProvider` shares the rail/drawer state between the sidebar and the
 * header's mobile trigger; the rail state stays the persisted
 * `useUiStore.sidebarCollapsed` preference (`admin-ui` in localStorage).
 */
export function AppShell() {
  const location = useLocation();
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const setCollapsed = useUiStore((s) => s.setSidebarCollapsed);
  return (
    <SidebarProvider collapsed={collapsed} onCollapsedChange={setCollapsed}>
      <ImpersonationBanner />
      <KitAppShell style={SHELL_STYLE} className="min-h-[calc(100dvh-var(--admin-banner-h,0px))]">
        <Sidebar />
        <Topbar />
        {/* FIX(admin-mobile-shell): Before: p-6 at every width. After: p-4 on
            phones (with the sidebar gone that is the full width minus 32px),
            p-6 from md up — desktop unchanged. The kit `AppShellContent` now
            supplies exactly that padding (1rem at 48rem and below, 1.5rem
            above); the inner wrapper only caps the width. */}
        <AppShellContent className="bg-muted/20">
          <div className="mx-auto max-w-[1400px]">
            {/* Per-route error boundary; resets on navigation. */}
            <ErrorBoundary resetKey={location.pathname}>
              <Suspense
                fallback={
                  <div className="flex h-64 items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
                  </div>
                }
              >
                <Outlet />
              </Suspense>
            </ErrorBoundary>
          </div>
        </AppShellContent>
      </KitAppShell>
      <CommandPalette />
      <IdleTimeout />
      <ShortcutsOverlay />
    </SidebarProvider>
  );
}
